<template>
    <AisInstantSearch
        :index-name="index"
        :search-client="algolia"
        :routing
        :future
    >
        <AisConfigure
            v-bind="aisConfigurationProps"
            :hits-per-page.camel="limit"
        />
        <slot />
    </AisInstantSearch>
</template>

<script setup>
import { history } from 'instantsearch.js/es/lib/routers';

import {
    AisInstantSearch,
    AisConfigure
} from 'vue-instantsearch/vue3/es/index.js';

// btoa/atob only handle Latin-1, and refinement values can contain other characters (e.g. ’).
// Encoding the UTF-8 bytes keeps ASCII-only output identical to plain btoa, so existing URLs keep working.
function encodeState(state) {
    const bytes = new TextEncoder().encode(JSON.stringify(state));

    return btoa(String.fromCharCode(...bytes));
}

function decodeState(encoded) {
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

    try {
        return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
    } catch {
        // URLs created before UTF-8 encoding hold Latin-1 bytes (e.g. ë)
        return JSON.parse(binary);
    }
}

const props = defineProps({
    indexName: {
        type: String,
        default: 'search'
    },
    limit: {
        type: Number,
        default: 20
    },
    filters: {
        type: String,
        default: undefined
    }
});

const { filters, indexName } = toRefs(props);

const { locale } = useI18n({
    useScope: 'local'
});

const index = computed(() => `STIMULERINGSFONDS_${indexName.value}_${locale.value}`);

const algolia = useAlgoliaRef();
const router = useRouter();
const route = useRoute();

const future = {
    preserveSharedStateOnUnmount: true
};

const aisConfigurationProps = reactive({});

watchEffect(() => {
    aisConfigurationProps.filters = filters.value;
});

const h = history({ cleanUrlOnDispose: false });
const routing = {
    router: {
        onUpdate: h.onUpdate,
        dispose: h.dispose,

        read() {
            const urlQuery = route.query;
            const state = {};

            if (urlQuery.q) {
                state.query = urlQuery.q;
            }

            if (urlQuery.refinementList) {
                state.refinementList = decodeState(urlQuery.refinementList);
            }

            if (urlQuery.menu) {
                state.menu = decodeState(urlQuery.menu);
            }

            state.page = urlQuery.page || 0;

            return {
                [index.value]: state
            };
        },

        write(routeState) {
            // Only push a new entry if the URL changed (avoid duplicated entries in the history)
            if (this.createURL(routeState) === this.createURL(this.read())) {
                return;
            }

            const query = {};

            if (routeState?.[index.value]?.query) {
                query.q = routeState[index.value].query;
            }

            if (routeState?.[index.value]?.page) {
                query.page = routeState[index.value].page;
            }

            if (routeState?.[index.value]?.refinementList) {
                query.refinementList = encodeState(routeState[index.value].refinementList);
            }

            if (routeState?.[index.value]?.menu) {
                query.menu = encodeState(routeState[index.value].menu);
            }

            router.push({ query });
        },

        createURL(routeState) {
            const query = {};

            if (routeState?.[index.value]?.query) {
                query.q = routeState[index.value].query;
            }

            if (routeState?.[index.value]?.page) {
                query.page = routeState[index.value].page;
            }

            if (routeState?.[index.value]?.refinementList) {
                query.refinementList = encodeState(routeState[index.value].refinementList);
            }

            if (routeState?.[index.value]?.menu) {
                query.menu = encodeState(routeState[index.value].menu);
            }

            return router.resolve({ query }).href;
        },
    }
};
</script>

<i18n lang="json">
{
    "nl": {
        "title": "Zoek",
        "type": "Type",
        "discipline": "Vakgebied",
        "theme": "Thema",
        "year": "Jaar",
        "grantsIssuedUrl": "/toekenningen",
        "placeholder": "zoekterm"
    },
    "en": {
        "title": "Search",
        "type": "Type",
        "discipline": "Discipline",
        "theme": "Theme",
        "year": "Year",
        "grantsIssuedUrl": "/en/grants-issued",
        "placeholder": "search term"
    }
}
</i18n>
